import { useEffect, useState } from "react";
import { Loader2, Save, MessageSquare } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const FIELD_LABEL =
  "text-xs font-semibold uppercase tracking-wider text-[#525252] mb-1.5 block";

export default function ScanMessages() {
  const [form, setForm] = useState({
    red_message: "",
    yellow_message: "",
    green_message: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/master/scan_messages");
        setForm({
          red_message: data?.red_message || "",
          yellow_message: data?.yellow_message || "",
          green_message: data?.green_message || "",
        });
      } catch (e) {
        toast.error(
          formatApiErrorDetail(e?.response?.data?.detail) ||
            "Failed to load scan messages"
        );
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/master/scan_messages", form);
      setForm({
        red_message: data?.red_message || "",
        yellow_message: data?.yellow_message || "",
        green_message: data?.green_message || "",
      });
      toast.success("Scan messages saved");
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) || "Save failed"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="scan-messages-page">
      <header className="bg-white border-b border-black/5 px-8 py-5">
        <h1 className="text-xl font-semibold text-[#0A0A0A] tracking-tight">
          Scan Messages
        </h1>
        <p className="text-xs text-[#525252] mt-0.5">
          Messages shown after scanning a cylinder, based on its testing-due status.
        </p>
      </header>

      <div className="p-8">
        <div className="bg-white border border-black/5 rounded-xl p-6 sm:p-8 max-w-3xl">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-[#F50E02]" />
            </div>
          ) : (
            <>
              <div className="mb-5 flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#F50E02]/10 flex items-center justify-center shrink-0">
                  <MessageSquare className="h-4 w-4 text-[#F50E02]" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-[#0A0A0A]">
                    Configure Scan Messages
                  </h2>
                  <p className="text-xs text-[#525252] mt-0.5">
                    These messages are shown when a cylinder QR is scanned and its
                    status is Red (overdue), Yellow (due within 30 days) or Green (ok).
                  </p>
                </div>
              </div>

              <div className="space-y-5">
                <div>
                  <Label className={FIELD_LABEL}>
                    <span className="inline-flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-[#F50E02]" />
                      Red Scan Message
                    </span>
                  </Label>
                  <Textarea
                    value={form.red_message}
                    onChange={(e) => setField("red_message", e.target.value)}
                    placeholder="e.g. Cylinder testing is overdue. Please take it for testing immediately."
                    rows={3}
                    className="bg-white border-black/10"
                    data-testid="red-message-input"
                  />
                </div>

                <div>
                  <Label className={FIELD_LABEL}>
                    <span className="inline-flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-amber-500" />
                      Yellow Scan Message
                    </span>
                  </Label>
                  <Textarea
                    value={form.yellow_message}
                    onChange={(e) => setField("yellow_message", e.target.value)}
                    placeholder="e.g. Cylinder testing is due within the next 30 days."
                    rows={3}
                    className="bg-white border-black/10"
                    data-testid="yellow-message-input"
                  />
                </div>

                <div>
                  <Label className={FIELD_LABEL}>
                    <span className="inline-flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-emerald-500" />
                      Green Scan Message
                    </span>
                  </Label>
                  <Textarea
                    value={form.green_message}
                    onChange={(e) => setField("green_message", e.target.value)}
                    placeholder="e.g. Cylinder is safe. Next testing is more than a month away."
                    rows={3}
                    className="bg-white border-black/10"
                    data-testid="green-message-input"
                  />
                </div>
              </div>

              <div className="mt-8 flex justify-end">
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-[#F50E02] hover:bg-[#D80C02] text-white"
                  data-testid="scan-messages-save"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
